/**
 * @jsxRuntime classic
 * @jsx el
 * @jsxFrag el.fragment
 */

import ElementController from '../../base-controllers/ElementController';
import { DATEPICKER_CONTROLLER_ID } from '../../../constants';
import {
  addDays,
  addMonths,
  addYears,
  formatDateYMD,
  getEndOfMonth,
  getEndOfWeek,
  getMonthNames,
  getStartOfMonth,
  getStartOfWeek,
  getWeekdayNames,
  isSameDay,
  isSameMonth,
  localeFormat,
  subtractDays,
  subtractMonths,
  subtractYears,
} from '../../utils/date';
import {
  el,
  getData,
  getInlineSVGAttrs,
  insertAfter,
  removeData,
  setData,
} from '../../utils/dom';
import { getRandomIntBetween } from '../../utils/js';
import { i18n } from '../../i18n';
import { getDocumentLang } from '../../utils/lang';
import sv from './Datepicker-sv';

i18n.addLocale('sv', sv);

// This monster from the comment at https://stackoverflow.com/a/37611386
// handles leap years correctly.
const YMD_DATE_PATTERN =
  '((([1-9]\\d{3})-(0[13578]|1[02])-(0[1-9]|[12]\\d|3[01]))|(((19|[2-9]\\d)\\d{2})-(0[13456789]|1[012])-(0[1-9]|[12]\\d|30))|(([1-9]\\d{3})-02-(0[1-9]|1\\d|2[0-8]))|(([1-9]\\d(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))-02-29))';
const NUM_ONLY_REGEX = /^\d+$/;

const LOCALE_DATA_ATTR = 'locale';
const BASE_CLASS_NAME_DATA_ATTR = 'base-classname';
const BASE_ID_DATA_ATTR = 'base-id';
const MIN_DATE_DATA_ATTR = 'min-date';
const MAX_DATE_DATA_ATTR = 'max-date';

/**
 * Controller for a form field date picker.
 *
 * Primarily based on the WAI-ARIA Authoring Practices example, with additional
 * inspiration from Duet Design System's accessible date picker.
 *
 * - https://w3c.github.io/aria-practices/examples/dialog-modal/datepicker-dialog.html
 * - https://github.com/duetds/date-picker
 *
 * The only required markup is a text field. It first tries to find one with
 * `datepicker-field` as its `data-node-name`, falling back to just `field`
 * and finally just the first text input.
 *
 *     <div data-controller="Datepicker">
 *       <label for="field-id">Date</label>
 *       <input type="text" id="field-id">
 *     </div>
 *
 * A trigger button will be inserted after the field and the picker dialog will
 * be inserted after the trigger. An existing trigger button can be supplied by
 * setting `datepicker-trigger` as the node name on a button.
 *
 *     <div data-controller="Datepicker">
 *       [...text field...]
 *       <button
 *         type="button"
 *         data-node-name="datepicker-trigger"
 *         aria-label="Choose date"
 *       >📅</button>
 *     </div>
 *
 * Any `aria-label` set on that button will be kept as the base label.
 *
 * Other options via data attributes on the controller root:
 *
 * - locale (string) Set which locale to use. Defaults to the document language
 *   (html `lang` attribute), or English if there's no `lang` set.
 * - base-classname (string) Prefix for class names, defaults to 'datepicker'.
 *   Note that the Sass must be changed as well if the default styles are used.
 * - base-id (string) Prefix for IDs, defaults to `id` from the text field or
 *   a randomly generated one if the field doesn't have an ID.
 * - min-date (string) Minimum allowed date, inclusive.
 * - max-date (string) Maximum allowed date, inclusive.
 */
export default class Datepicker extends ElementController {
  static id = DATEPICKER_CONTROLLER_ID;

  init() {
    // Base elements
    this.textField = this.selectSingle('datepicker-field');
    if (!this.textField) {
      // Fall back to just `field` that's used by the Form controller
      this.textField = this.selectSingle('field');
      if (!this.textField) {
        // Further fall back to the first text input
        this.textField = this.selectSingle('input[type="text"]');
      }
    }
    if (!this.textField) {
      throw new Error('Datepicker requires a text field child');
    }
    this.triggerButton = this.selectSingle('datepicker-trigger');
    this.hasCustomTriggerButton = true;
    if (!this.triggerButton) {
      this.hasCustomTriggerButton = false;
      this.triggerButton = this.insertTriggerButton();
    }

    // Locale
    const locale = getData(this.root, LOCALE_DATA_ATTR) || getDocumentLang();
    this.locale = locale;

    // Screen reader labels.
    this.dayLabels = getWeekdayNames('long', locale);
    this.dayLabelsShort = getWeekdayNames('short', locale);
    this.monthLabels = getMonthNames('long', locale);
    this.defaultTriggerLabel =
      this.triggerButton.getAttribute('aria-label') || i18n.t('Choose date');
    this.lastInstruction = '';

    // Misc. settings.
    this.baseClassname =
      getData(this.root, BASE_CLASS_NAME_DATA_ATTR) || 'datepicker';
    this.baseId =
      getData(this.root, BASE_ID_DATA_ATTR) ||
      this.textField.id ||
      `datepicker-${getRandomIntBetween(1, Number.MAX_SAFE_INTEGER - 1)}`;

    // Objects with `cell` (the table cell node) and `date` (Date object).
    // Populated in insertDialog, modified in updateGrid.
    this.days = [];

    this.setFocusedAndSelectedFromTextField();
    this.setMinMaxDates();

    // Create all the necessary markup
    this.insertDialog((dialog) => {
      this.dialog = dialog;
      this.tbody = this.selectSingle(dialog, 'tbody');
      this.instructions = this.selectSingle(dialog, 'instructions');
      this.dialogTitle = this.selectSingle(dialog, 'dialog-title');
      this.prevYearButton = this.selectSingle(dialog, 'prev-year');
      this.prevMonthButton = this.selectSingle(dialog, 'prev-month');
      this.nextMonthButton = this.selectSingle(dialog, 'next-month');
      this.nextYearButton = this.selectSingle(dialog, 'next-year');
      this.closeButton = this.selectSingle(dialog, 'close');

      this.updateCalendar();
      this.addBaseAttributes();
      this.bindEvents();
      this.updateFocusedCell();
      this.setTriggerLabel();
    });
  }

  /**
   * Undo DOM changes and unbind events when destroyed.
   */
  destroy() {
    this.close(false);
    this.removeBaseAttributes();
    this.unbindEvents();

    // Remove elements
    this.dialog.remove();
    if (!this.hasCustomTriggerButton) {
      this.triggerButton.remove();
    }
  }

  /**
   * Create and insert a dialog trigger button.
   *
   * @returns {HTMLElement} - The inserted button.
   */
  insertTriggerButton() {
    const btn = (
      <button
        type="button"
        data-node-name="datepicker-trigger"
        aria-label={this.defaultTriggerLabel}
      >
        {/* Calendar icon */}
        <svg {...getInlineSVGAttrs(16, 16)}>
          <path d="M5 6h2v2H5zm3 0h2v2H8zm3 0h2v2h-2zm-9 6h2v2H2zm3 0h2v2H5zm3 0h2v2H8zM5 9h2v2H5zm3 0h2v2H8zm3 0h2v2h-2zM2 9h2v2H2zm11-9v1h-2V0H4v1H2V0H0v16h15V0h-2zm1 15H1V4h13v11z" />
        </svg>
      </button>
    );
    insertAfter(this.textField, btn);
    return btn;
  }

  /**
   * Create and insert the datepicker dialog.
   *
   * @param {Function} done - Callback for when the dialog has been inserted.
   *   Is passed the dialog element.
   */
  insertDialog(done) {
    const baseClass = this.baseClassname;
    const dialogTitleId = `${this.baseId}-dialog-title`;
    const getButtonClass = (nodeName, extra = '') =>
      `${baseClass}-button ${baseClass}-${nodeName} ${extra}`.trim();
    const navButtonExtraClass = `${baseClass}-nav-button`;

    // Dialog skeleton.
    const tbody = <tbody />;
    const Chevron = () => (
      <svg {...getInlineSVGAttrs(24, 24)}>
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
      </svg>
    );
    const Button = (props) => (
      <button
        type="button"
        aria-label={props['aria-label']}
        data-node-name={props['data-node-name']}
        class={getButtonClass(
          props['data-node-name'],
          props.isNav ? navButtonExtraClass : '',
        )}
      >
        {props.children}
      </button>
    );
    const dialog = (
      <div
        data-node-name="datepicker-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        aria-hidden="true"
        class={`${baseClass}-dialog`}
      >
        <div class={`${baseClass}-header`}>
          <Button
            data-node-name="prev-year"
            aria-label={i18n.t('Previous year')}
            isNav
          >
            <Chevron />
            <Chevron />
          </Button>
          <Button
            data-node-name="prev-month"
            aria-label={i18n.t('Previous month')}
            isNav
          >
            <Chevron />
          </Button>
          <h2
            data-node-name="dialog-title"
            aria-live="polite"
            aria-atomic="true"
            class={`${baseClass}-dialog-title`}
            id={dialogTitleId}
          />
          <Button
            data-node-name="next-month"
            aria-label={i18n.t('Next month')}
            isNav
          >
            <Chevron />
          </Button>
          <Button
            data-node-name="next-year"
            aria-label={i18n.t('Next year')}
            isNav
          >
            <Chevron />
            <Chevron />
          </Button>
        </div>
        <table
          role="grid"
          aria-labelledby={dialogTitleId}
          class={`${baseClass}-dates`}
        >
          <thead>
            <tr>
              {this.dayLabels.map((dayLabel, i) => (
                <th scope="col" abbr={dayLabel}>
                  {this.dayLabelsShort[i]}
                </th>
              ))}
            </tr>
          </thead>
          {tbody}
        </table>
        <Button data-node-name="close">{i18n.t('Close date picker')}</Button>
        <div
          data-node-name="instructions"
          class={`${baseClass}-instructions`}
          aria-live="polite"
        />
      </div>
    );

    // Add cells. Outside the above JSX since inserted elements are also added
    // to internal class properties.
    this.lastTableRow = null;
    for (let i = 0; i < 6; i += 1) {
      const row = tbody.insertRow(i);
      this.lastTableRow = row;
      for (let j = 0; j < 7; j += 1) {
        const cell = <td tabindex="-1">?</td>;
        row.append(cell);
        this.days.push({ cell, date: null });
      }
    }

    // Use requestAnimationFrame to hopefully keep performance nice even if
    // there are multiple pickers on the same page.
    requestAnimationFrame(() => {
      insertAfter(this.triggerButton, dialog);
      done(dialog);
    });
  }

  /**
   * Check if a date is outside specified min date, if any.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isDateOutsideMin(date) {
    return this.minDate && date.getTime() < this.minDate.getTime();
  }

  /**
   * Check if a date is outside specified min date, if any.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isDateOutsideMax(date) {
    return this.maxDate && date.getTime() > this.maxDate.getTime();
  }

  /**
   * Check if a date is outside specified min/max range.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isDateOutsideMinMax(date) {
    return this.isDateOutsideMin(date) || this.isDateOutsideMax(date);
  }

  /**
   * Enable or disable prev/next buttons depending on min/max dates.
   */
  checkNavigationButtons() {
    const min = this.minDate;
    const max = this.maxDate;
    const focused = this.focusedDay;
    if (min) {
      this.prevYearButton.disabled = subtractYears(focused, 1) < min;
      this.prevMonthButton.disabled =
        getEndOfMonth(subtractMonths(focused, 1)) < min;
    }
    if (max) {
      this.nextYearButton.disabled = addYears(focused, 1) > max;
      this.nextMonthButton.disabled =
        getStartOfMonth(addMonths(focused, 1)) > max;
    }
  }

  /**
   * Update the displayed dates in the calendar grid based on the currently
   * focused day.
   */
  updateCalendar() {
    const today = new Date();
    const focusedMonth = this.focusedDay.getMonth();
    const focusedYear = this.focusedDay.getFullYear();

    this.dialogTitle.textContent = `${this.monthLabels[focusedMonth]} ${focusedYear}`;

    // Start at the first day of the month and go back to the closest Monday.
    // The day of the week (getDay) is zero based (0 to 6) with Sunday as the
    // first day (Americans...) - get a diff for Monday as the first.
    const firstCellDate = new Date(focusedYear, focusedMonth, 1);
    const dayOfWeek = firstCellDate.getDay();
    const dayOfWeekDiff = dayOfWeek >= 1 ? dayOfWeek - 1 : 6 - dayOfWeek;
    firstCellDate.setDate(firstCellDate.getDate() - dayOfWeekDiff);

    const outsideMonthClass = `${this.baseClassname}-day-outside-month`;
    const disabledClass = `${this.baseClassname}-day-disabled`;
    for (let i = 0; i < this.days.length; i += 1) {
      const cellDay = new Date(
        firstCellDate.getFullYear(),
        firstCellDate.getMonth(),
        firstCellDate.getDate() + i,
      );
      const { cell } = this.days[i];
      this.days[i].date = cellDay;
      cell.textContent = cellDay.getDate();

      // Add class to days outside the current month.
      if (cellDay.getMonth() !== focusedMonth) {
        cell.classList.add(outsideMonthClass);
      } else {
        cell.classList.remove(outsideMonthClass);
      }

      // Add class to days outside the current month.
      if (this.isDateOutsideMinMax(cellDay)) {
        cell.classList.add(disabledClass);
      } else {
        cell.classList.remove(disabledClass);
      }

      // Mark the selected day
      if (isSameDay(cellDay, this.selectedDay)) {
        cell.setAttribute('aria-selected', 'true');
      } else {
        cell.removeAttribute('aria-selected');
      }

      // Mark the current day
      if (isSameDay(cellDay, today)) {
        cell.setAttribute('aria-current', 'date');
      } else {
        cell.removeAttribute('aria-current');
      }
    }

    // Hide the last row if it only contains days from the next month
    if (
      this.lastTableRow.firstElementChild.classList.contains(outsideMonthClass)
    ) {
      this.lastTableRow.setAttribute('hidden', '');
    } else {
      this.lastTableRow.removeAttribute('hidden');
    }

    this.checkNavigationButtons();
  }

  /**
   * Set the currently focused date if it's within min/max.
   *
   * @param {Date} newDate - The date to set as focused.
   * @param {boolean} [clamp] - Clamp the date to min/max if it's outside.
   *   Defaults to false where the focus simply won't move.
   * @param {boolean} [moveCellFocus] - Also move actual keyboard focus to the
   *   new date's cell, defaults to false.
   */
  setFocusedDay(newDate, clamp = false, moveCellFocus = false) {
    /* eslint-disable no-param-reassign */

    if (this.isDateOutsideMinMax(newDate)) {
      if (!clamp) {
        return;
      }
      if (this.isDateOutsideMin(newDate)) {
        newDate = this.minDate;
      } else if (this.isDateOutsideMax(newDate)) {
        newDate = this.maxDate;
      }
    }

    const currentlyFocused = this.focusedDay;

    this.focusedDay = newDate;

    if (!isSameMonth(currentlyFocused, newDate)) {
      this.updateCalendar();
    }

    if (moveCellFocus) {
      this.updateFocusedCell();
    }
  }

  /**
   * Set class names on base elements.
   *
   * @param {string} classListMethod - Method for classList, 'add' or 'remove'.
   */
  setBaseClassNames(classListMethod) {
    const baseClass = this.baseClassname;
    this.root.classList[classListMethod](baseClass);
    this.textField.classList[classListMethod](`${baseClass}-field`);
    this.triggerButton.classList[classListMethod](
      `${baseClass}-button`,
      `${baseClass}-trigger`,
    );
  }

  /**
   * Add attributes to base elements.
   */
  addBaseAttributes() {
    this.setBaseClassNames('add');
    if (!this.textField.pattern) {
      this.hasCustomTextFieldPattern = true;
      this.textField.pattern = YMD_DATE_PATTERN;
    }
  }

  /**
   * Remove attributes from base elements.
   */
  removeBaseAttributes() {
    this.setBaseClassNames('remove');
    if (this.hasCustomTextFieldPattern) {
      this.textField.pattern = '';
    }
  }

  /**
   * Bind all dialog events.
   */
  bindEvents() {
    this.on(this.textField, 'keyup', this.handleTextFieldKeyup);
    this.on(this.textField, 'blur', this.handleTextFieldBlur);

    this.on(this.triggerButton, 'click', this.handleTriggerClick);

    this.on(this.dialog, 'keydown', this.handleDialogKeydown);
    this.on(document, 'click', this.handleOutsideClick);

    this.on(this.prevYearButton, 'click', this.handlePrevYearClick);
    this.on(this.prevYearButton, 'keydown', this.handlePrevYearKeydown);
    this.on(this.prevMonthButton, 'click', this.handlePrevMonthClick);
    this.on(this.prevMonthButton, 'keydown', this.handlePrevMonthKeydown);
    this.on(this.nextMonthButton, 'click', this.handleNextMonthClick);
    this.on(this.nextMonthButton, 'keydown', this.handleNextMonthKeydown);
    this.on(this.nextYearButton, 'click', this.handleNextYearClick);

    this.on(this.tbody, 'keydown', 'td', this.handleCellKeydown);
    this.on(this.tbody, 'click', 'td', this.handleCellClick);
    // The `focus` event doesn't bubble and so can't be delegated.
    this.on(this.tbody, 'focusin', 'td', this.handleCellFocus);

    this.on(this.closeButton, 'click', this.handleCloseClick);
    this.on(this.closeButton, 'keydown', this.handleCloseKeydown);
  }

  /**
   * Unbind all dialog events.
   */
  unbindEvents() {
    this.off(this.textField, 'keyup', this.handleTextFieldKeyup);
    this.off(this.textField, 'blur', this.handleTextFieldBlur);

    this.off(this.triggerButton, 'click', this.handleTriggerClick);

    this.off(this.dialog, 'keydown', this.handleDialogKeydown);
    this.off(document, 'click', this.handleOutsideClick);

    this.off(this.prevYearButton, 'click', this.handlePrevYearClick);
    this.off(this.prevYearButton, 'keydown', this.handlePrevYearKeydown);
    this.off(this.prevMonthButton, 'click', this.handlePrevMonthClick);
    this.off(this.prevMonthButton, 'keydown', this.handlePrevMonthKeydown);
    this.off(this.nextMonthButton, 'click', this.handleNextMonthClick);
    this.off(this.nextMonthButton, 'keydown', this.handleNextMonthKeydown);
    this.off(this.nextYearButton, 'click', this.handleNextYearClick);

    this.off(this.tbody, 'keydown', this.handleCellKeydown);
    this.off(this.tbody, 'click', this.handleCellClick);
    this.off(this.tbody, 'focusin', this.handleCellFocus);

    this.off(this.closeButton, 'click', this.handleCloseClick);
    this.off(this.closeButton, 'keydown', this.handleCloseKeydown);
  }

  /**
   * Update the cell for the currently focused day.
   *
   * @param {boolean} [moveFocusToCell] - Also move keyboard focus to the
   *   focused cell, defaults to true.
   */
  updateFocusedCell(moveFocusToCell = true) {
    this.days.forEach((day) => {
      day.cell.tabIndex = -1;
      if (isSameDay(day.date, this.focusedDay)) {
        day.cell.tabIndex = 0;
        if (moveFocusToCell) {
          day.cell.focus();
        }
      }
    });
  }

  /**
   * Set min and max date properties from controller root attributes.
   */
  setMinMaxDates() {
    // The date constructor parsing (Date.parse) takes the local timezone into
    // account which doesn't happen when using individual parameters like the
    // calendar cells do. This can cause the cells to have dates at midnight
    // while min and max may have 01:00 or 02:00 or something else entirely
    // depending on timezone and daylight savings. Reset the time to combat
    // this.
    let minDate = getData(this.root, MIN_DATE_DATA_ATTR);
    let maxDate = getData(this.root, MAX_DATE_DATA_ATTR);
    if (minDate) {
      minDate = new Date(minDate);
      minDate.setHours(0, 0, 0, 0);
    }
    if (maxDate) {
      maxDate = new Date(maxDate);
      maxDate.setHours(0, 0, 0, 0);
    }
    if (minDate && maxDate && minDate > maxDate) {
      throw new Error("Datepicker min date can't be after max date");
    }
    this.minDate = minDate;
    this.maxDate = maxDate;
  }

  /**
   * Check if the dialog is currently open.
   *
   * @readonly
   * @returns {boolean}
   */
  get isOpen() {
    return this.dialog.getAttribute('aria-hidden') === 'false';
  }

  /**
   * Open the dialog.
   */
  open() {
    this.dialog.setAttribute('aria-hidden', 'false');
    this.setMinMaxDates();
    this.setFocusedAndSelectedFromTextField();
    this.updateCalendar();
    this.updateFocusedCell();
  }

  /**
   * Close the dialog.
   *
   * @param {boolean} focusTriggerButton - If focus should be moved to the
   *   trigger button, defaults to true.
   */
  close(focusTriggerButton = true) {
    this.setInstructionMessage('');
    this.dialog.setAttribute('aria-hidden', 'true');
    if (focusTriggerButton) {
      this.triggerButton.focus();
    }
  }

  /**
   * Move focus to the next year and update the calendar.
   *
   * @param {boolean} [moveCellFocus] - Also move actual keyboard focus to the
   *   new date's cell, defaults to false.
   */
  focusNextYear(moveCellFocus = false) {
    this.setFocusedDay(addYears(this.focusedDay, 1), true, moveCellFocus);
  }

  /**
   * Move focus to the previous year and update the calendar.
   *
   * @param {boolean} [moveCellFocus] - Also move actual keyboard focus to the
   *   new date's cell, defaults to false.
   */
  focusPreviousYear(moveCellFocus = false) {
    this.setFocusedDay(subtractYears(this.focusedDay, 1), true, moveCellFocus);
  }

  /**
   * Move focus to the next month and update the calendar.
   *
   * @param {boolean} [moveCellFocus] - Also move actual keyboard focus to the
   *   new date's cell, defaults to false.
   */
  focusNextMonth(moveCellFocus = false) {
    this.setFocusedDay(addMonths(this.focusedDay, 1), true, moveCellFocus);
  }

  /**
   * Move focus to the previous month and update the calendar.
   *
   * @param {boolean} [moveCellFocus] - Also move actual keyboard focus to the
   *   new date's cell, defaults to false.
   */
  focusPreviousMonth(moveCellFocus = false) {
    this.setFocusedDay(subtractMonths(this.focusedDay, 1), true, moveCellFocus);
  }

  /**
   * Move focus to the day after the currently focused one.
   */
  focusNextDay() {
    this.setFocusedDay(addDays(this.focusedDay, 1), false, true);
  }

  /**
   * Move focus to the same weekday in the week following the currently
   * focused one.
   */
  focusNextWeek() {
    this.setFocusedDay(addDays(this.focusedDay, 7), false, true);
  }

  /**
   * Move focus to the day before the currently focused one.
   */
  focusPreviousDay() {
    this.setFocusedDay(subtractDays(this.focusedDay, 1), false, true);
  }

  /**
   * Move focus to the same weekday in the week before the currently
   * focused one.
   */
  focusPreviousWeek() {
    this.setFocusedDay(subtractDays(this.focusedDay, 7), false, true);
  }

  /**
   * Move focus to the Monday of the currently focused day's week.
   */
  focusFirstDayOfWeek() {
    this.setFocusedDay(getStartOfWeek(this.focusedDay), false, true);
  }

  /**
   * Move focus to the Sunday of the currently focused day's week.
   */
  focusLastDayOfWeek() {
    this.setFocusedDay(getEndOfWeek(this.focusedDay), false, true);
  }

  /**
   * Set date properties based on the text field value.
   */
  setFocusedAndSelectedFromTextField() {
    const date = this.getTextFieldDate();

    // Default to focus on today's date and selected as a 'beginning of time'
    // (results in January 1, 1900).
    let focusedDay = new Date();
    let selectedDay = new Date(0, 0, 1);
    if (date) {
      focusedDay = date;
    }

    // Clamp if outside min/max. Only set selected if there is a valid date in
    // the text field and it's within min/max.
    if (this.isDateOutsideMin(focusedDay)) {
      focusedDay = this.minDate;
    } else if (this.isDateOutsideMax(focusedDay)) {
      focusedDay = this.maxDate;
    } else if (date) {
      selectedDay = new Date(focusedDay);
    }

    this.focusedDay = focusedDay;
    this.selectedDay = selectedDay;
  }

  /**
   * Set a help message for screen readers.
   *
   * @param {string} msg - The message.
   */
  setInstructionMessage(msg) {
    if (msg !== this.lastInstruction) {
      setTimeout(() => {
        this.instructions.textContent = msg;
      }, 100);
      this.lastInstruction = msg;
    }
  }

  /**
   * Set the trigger button's aria-label, including the currently selected
   * date if any.
   */
  setTriggerLabel() {
    const labelParts = [this.defaultTriggerLabel];
    const date = this.getTextFieldDate();
    if (date) {
      const formattedDate = localeFormat(
        date,
        { year: 'numeric', month: 'long', day: 'numeric' },
        this.locale,
      );
      labelParts.push(
        i18n.t('selected date is {{date}}').replace('{{date}}', formattedDate),
      );
    }
    this.triggerButton.setAttribute('aria-label', labelParts.join(', '));
  }

  /**
   * Get the date currently in the text field, if any.
   *
   * @returns {?Date} - May be null if the field is empty or doesn't contain
   *   a valid date.
   */
  getTextFieldDate() {
    const parts = this.textField.value
      .split('-')
      .map((part) => parseInt(part, 10));

    return parts.length === 3 && parts.every((part) => Number.isInteger(part))
      ? new Date(parts[0], parts[1] - 1, parts[2])
      : null;
  }

  /**
   * Set the specified date as the text field value.
   *
   * @param {Date} day
   */
  setTextFieldDate(day) {
    this.textField.value = formatDateYMD(day);
    this.setTriggerLabel();
    this.setTextFieldValidity();

    // Trigger a blur event on the text field as if the new date has just
    // been typed in and the field left.
    this.textField.dispatchEvent(
      new FocusEvent('blur', {
        view: globalThis,
        bubbles: true,
        cancelable: false,
      }),
    );
  }

  /**
   * Set the specified date as the text field value.
   */
  setTextFieldValidity() {
    // Don't use this.focusedDay since it's clamped to min/max
    const date = this.getTextFieldDate();
    if (this.isDateOutsideMinMax(date)) {
      setData(this.textField, 'validity', 'invalid');
    } else {
      removeData(this.textField, 'validity');
    }
  }

  /**
   * Select the date from the specified table cell and close the dialog.
   *
   * @param {HTMLTableCellElement} cellNode
   */
  selectCellDate(cellNode) {
    const i = this.days.findIndex(({ cell }) => cell === cellNode);
    if (i === -1) {
      throw new Error('Datepicker cell not found');
    }
    const targetDate = this.days[i].date;
    if (!this.isDateOutsideMinMax(targetDate)) {
      this.setTextFieldDate(targetDate);
      this.close();
    }
  }

  /**
   * Get the first active (i.e. not disabled) navigation button.
   *
   * @param {boolean} [fromRight] - If the search should be from the next
   *   buttons to the right instead of the previous buttons to the left.
   * @returns {?HTMLButtonElement} - Null if all are disabled.
   */
  getClosestActiveButton(fromRight = false) {
    const buttons = [
      this.prevYearButton,
      this.prevMonthButton,
      this.nextMonthButton,
      this.nextYearButton,
    ];
    if (fromRight) {
      buttons.reverse();
    }
    return buttons.find((btn) => !btn.disabled);
  }

  /**
   * Add dashes to the date field if it seems to be a date without them.
   */
  handleTextFieldKeyup = () => {
    const val = String(this.textField.value).trim();
    if (val.length === 8 && NUM_ONLY_REGEX.test(val)) {
      this.textField.value = `${val.slice(0, 4)}-${val.slice(4, 6)}-${val.slice(
        6,
      )}`;
    }
  };

  /**
   * Update the dates and trigger button label when leaving the text field and
   * check if the date is within min/max.
   */
  handleTextFieldBlur = () => {
    this.setFocusedAndSelectedFromTextField();
    this.setTriggerLabel();
    this.setTextFieldValidity();
  };

  /**
   * Open or close the dialog when pressing the trigger button.
   *
   * @param {MouseEvent} e
   */
  handleTriggerClick = (e) => {
    e.preventDefault();
    if (!this.isOpen) {
      this.open();
      this.updateFocusedCell();
    } else {
      this.close();
    }
  };

  /**
   * Close the dialog when pressing escape.
   *
   * @param {KeyboardEvent} e
   */
  handleDialogKeydown = (e) => {
    if (e.key === 'Escape') {
      this.close();
    }
  };

  /**
   * Close the dialog when clicking outside it.
   *
   * @param {MouseEvent} e - Click event.
   */
  handleOutsideClick = (e) => {
    // The toggle has the necessary 'flags' and the click target is not the
    // toggle button or the toggle target.
    if (
      this.isOpen &&
      !this.dialog.contains(e.target) &&
      !this.triggerButton.contains(e.target)
    ) {
      this.close(false);
    }
  };

  /**
   * Move to the previous year when pressing the previous year button.
   */
  handlePrevYearClick = () => {
    this.focusPreviousYear();
    this.updateFocusedCell(false);
  };

  /**
   * Keep focus in the dialog when tabbing backwards from the previous year
   * button.
   *
   * @param {KeyboardEvent} e
   */
  handlePrevYearKeydown = (e) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      this.closeButton.focus();
    }
  };

  /**
   * Move to the previous month when pressing the previous month button.
   */
  handlePrevMonthClick = () => {
    this.focusPreviousMonth();
    this.updateFocusedCell(false);
  };

  /**
   * Keep focus in the dialog when tabbing backwards from the previous month
   * button.
   *
   * @param {KeyboardEvent} e
   */
  handlePrevMonthKeydown = (e) => {
    if (e.key === 'Tab' && e.shiftKey && this.prevYearButton.disabled) {
      e.preventDefault();
      this.closeButton.focus();
    }
  };

  /**
   * Move to the next month when pressing the next month button.
   */
  handleNextMonthClick = () => {
    this.focusNextMonth();
    this.updateFocusedCell(false);
  };

  /**
   * Keep focus in the dialog when tabbing backwards from the next month
   * button.
   *
   * @param {KeyboardEvent} e
   */
  handleNextMonthKeydown = (e) => {
    if (
      e.key === 'Tab' &&
      e.shiftKey &&
      this.prevYearButton.disabled &&
      this.prevMonthButton.disabled
    ) {
      e.preventDefault();
      this.closeButton.focus();
    }
  };

  /**
   * Move to the next year when pressing the next year button.
   */
  handleNextYearClick = () => {
    this.focusNextYear();
    this.updateFocusedCell(false);
  };

  /**
   * Handle navigation in the calendar grid.
   *
   * @param {KeyboardEvent} e
   */
  handleCellKeydown = (e) => {
    let isHandled = false;

    switch (e.key) {
      case 'Tab': {
        const activeButton = this.getClosestActiveButton(true);
        if (e.shiftKey && activeButton) {
          activeButton.focus();
        } else {
          this.closeButton.focus();
        }
        this.setInstructionMessage('');
        isHandled = true;
        break;
      }

      case 'Enter': {
        this.selectCellDate(e.target);
        isHandled = true;
        break;
      }

      case 'ArrowRight':
        this.focusNextDay();
        isHandled = true;
        break;

      case 'ArrowLeft':
        this.focusPreviousDay();
        isHandled = true;
        break;

      case 'ArrowDown':
        this.focusNextWeek();
        isHandled = true;
        break;

      case 'ArrowUp':
        this.focusPreviousWeek();
        isHandled = true;
        break;

      case 'PageUp':
        if (e.shiftKey) {
          this.focusPreviousYear(true);
        } else {
          this.focusPreviousMonth(true);
        }
        isHandled = true;
        break;

      case 'PageDown':
        if (e.shiftKey) {
          this.focusNextYear(true);
        } else {
          this.focusNextMonth(true);
        }
        isHandled = true;
        break;

      case 'Home':
        this.focusFirstDayOfWeek();
        isHandled = true;
        break;

      case 'End':
        this.focusLastDayOfWeek();
        isHandled = true;
        break;

      default:
        // No default
        break;
    }

    if (isHandled) {
      e.preventDefault();
    }
  };

  /**
   * Select the date from clicked calendar cells.
   *
   * @param {MouseEvent} e
   */
  handleCellClick = (e) => {
    e.preventDefault();
    this.selectCellDate(e.target);
  };

  /**
   * Add a help text for screen readers describing the keyboard navigation
   * when a calendar cell receives focus.
   */
  handleCellFocus = () => {
    this.setInstructionMessage(
      i18n.t('You can navigate dates with the arrow keys'),
    );
  };

  /**
   * Close the dialog when pressing the close button.
   */
  handleCloseClick = () => {
    this.close();
  };

  /**
   * Keep focus in the dialog when tabbing forwards from the close button.
   *
   * @param {KeyboardEvent} e
   */
  handleCloseKeydown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const activeButton = this.getClosestActiveButton();
      e.preventDefault();
      if (activeButton) {
        activeButton.focus();
      } else {
        const day = this.days.find(({ date }) =>
          isSameDay(date, this.focusedDay),
        );
        if (day) {
          day.cell.focus();
        }
      }
    }
  };
}
